import { useState } from "react";
import { useDropzone } from "react-dropzone";
import api from "../api";
import Swal from "sweetalert2";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Send } from "lucide-react";

function Support() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Handle email chips
  const handleEmailInput = (e) => setEmailInput(e.target.value);
  const handleEmailKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && emailInput.trim()) {
      e.preventDefault();
      if (!emails.includes(emailInput.trim())) {
        setEmails([...emails, emailInput.trim()]);
      }
      setEmailInput("");
    }
    if (e.key === "Backspace" && !emailInput && emails.length) {
      setEmails(emails.slice(0, -1));
    }
  };
  const handleEmailBlur = () => {
    if (emailInput.trim() && !emails.includes(emailInput.trim())) {
      setEmails([...emails, emailInput.trim()]);
      setEmailInput("");
    }
  }
  const removeEmail = (idx) => setEmails(emails.filter((_, i) => i !== idx));

  // Dropzone for attachments
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files) => setAttachments([...attachments, ...files]),
    accept: {
      "application/pdf": [],
      "image/*": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
    },
    maxFiles: 5,
  });
  const removeAttachment = (idx) =>
    setAttachments(attachments.filter((_, i) => i !== idx));

  // Send mail
  const sendMail = async (e) => {
    e.preventDefault();
    if (!subject || !message || emails.length === 0) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all required fields.",
        icon: "warning",
      });
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("subject", subject);
    formData.append("message", message);
    emails.forEach((email) => formData.append("emails", email));
    attachments.forEach((file) => formData.append("attachments", file));
    try {
      const resp = await api.post("/contact/sendmail", formData);
      if (resp.status === 200) {
        Swal.fire({
          title: "Success!",
          text: "Mail Sent Successfully!",
          icon: "success",
        });
        setSubject("");
        setMessage("");
        setEmails([]);
        setAttachments([]);
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data || "Something went wrong.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      style={{
        background: "rgba(255,255,255,0.96)",
        padding: 0.5,
        position: "relative",
      }}
    >
      <Typography
        variant="h4"
        style={{
          marginBottom: 18,
          color: "#3b3b5c",
          letterSpacing: 1,
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        Send Your Query
      </Typography>
      <Typography
        variant="body1"
        style={{ marginBottom: 18, color: "#3b3b5c" }}
      >
        If you have any questions or need assistance, please fill out the form
        below and we will get back to you as soon as possible.
      </Typography>
      <form onSubmit={sendMail} autoComplete="off" width="100%">
        <Stack direction="row" flexWrap="wrap" gap={2}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            {/* Recipient emails */}
            <label style={{ fontWeight: 500, fontSize: 15 }}>To</label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                border: "1.5px solid #b6c2e2",
                borderRadius: 8,
                padding: "6px 10px",
                marginBottom: 18,
                background: "#f6f8fa",
              }}
            >
              {emails.map((email, idx) => (
                <span
                  key={idx}
                  style={{
                    background: "#6366f1",
                    color: "#fff",
                    borderRadius: 16,
                    padding: "4px 12px",
                    margin: "2px 6px 2px 0",
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: 14,
                  }}
                >
                  {email}
                  <span
                    onClick={() => removeEmail(idx)}
                    style={{
                      marginLeft: 8,
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    &times;
                  </span>
                </span>
              ))}
              <input
                type="email"
                value={emailInput}
                onChange={handleEmailInput}
                onBlur={handleEmailBlur}
                onKeyDown={handleEmailKeyDown}
                placeholder="support@gmail.com"
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: 15,
                  minWidth: 120,
                  background: "transparent",
                  padding: 4,
                }}
              />
            </div>

            {/* Subject */}
            <label style={{ fontWeight: 500, fontSize: 15 }}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              placeholder="Enter subject"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1.5px solid #b6c2e2",
                marginBottom: 18,
                fontSize: 15,
                background: "#f6f8fa",
              }}
            />
            {/* Attachments */}
            <label style={{ fontWeight: 500, fontSize: 15 }}>Attachments</label>
            <div
              {...getRootProps()}
              style={{
                border: "1.5px dashed #b6c2e2",
                borderRadius: 8,
                padding: "18px 0",
                textAlign: "center",
                color: "#64748b",
                background: "#f8fafc",
                cursor: "pointer",
                marginBottom: 10,
              }}
            >
              <input {...getInputProps()} />
              <span style={{ fontSize: 14 }}>
                Drag & drop files here, or click to select (max 5)
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 20,
              }}
            >
              {attachments.map((file, idx) => (
                <span
                  key={idx}
                  style={{
                    background: "#e0e7ff",
                    borderRadius: 12,
                    padding: "6px 12px",
                    fontSize: 14,
                    color: "#3b3b5c",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {file.name}
                  <span
                    onClick={() => removeAttachment(idx)}
                    style={{
                      marginLeft: 8,
                      color: "#ef4444",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    &times;
                  </span>
                </span>
              ))}
            </div>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            {/* Message */}
            <label style={{ fontWeight: 500, fontSize: 15 }}>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              placeholder="Type your message..."
              style={{
                width: "100%",
                minHeight: 200,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1.5px solid #b6c2e2",
                marginBottom: 18,
                fontSize: 15,
                background: "#f6f8fa",
                resize: "vertical",
              }}
            />
          </Box>
        </Stack>

        {/* Send button */}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={isLoading}
          startIcon={<Send size={16} />}
        >
          {isLoading ? "Sending..." : "Send Query"}
        </Button>
      </form>
    </Box>
  );
}

export default Support;
